import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import './About.css';
import { Helmet } from 'react-helmet';

const History = () => {
  const milestones = [
    {
      year: '2010',
      title: 'The Foundation Year',
      description: 'Imperial Badminton Academy was established in 2010 with a simple yet powerful vision: to provide quality badminton coaching rooted in discipline and correct technique. During the early years, training focused on basic skill development, fitness, and creating awareness about professional badminton training among young players. Despite limited resources, the academy laid a strong foundation built on passion, commitment, and personal attention to each trainee.'
    },
    {
      year: '2013',
      title: 'First State Champions',
      description: 'Our students won their first state-level championship, validating our training methodology. This victory put Imperial on the map as a serious training academy.'
    },
    {
      year: '2016',
      title: 'Growth and Recognition',
      description: 'As player strength increased, Imperial Badminton Academy expanded its training structure. More players started participating in local and district-level tournaments, gaining valuable match exposure. This period brought recognition for the academy’s coaching quality and training discipline. Player confidence grew, and the academy began to be known for producing technically sound and well-prepared athletes.'
    },
    {
      year: '2018',
      title: 'Professional Training Approach',
      description: 'During these years, the academy adopted a more professional and scientific training methodology. Fitness training, agility drills, and match strategy sessions were integrated into daily practice. Players were trained not only to play well but also to understand the game tactically. Mental strength, consistency, and match temperament became key focus areas.'
    },
    {
      year: '2019',
      title: 'Expanding Competitive Focus',
      description: 'By 2019, Imperial Badminton Academy had established itself as a serious training center for competitive badminton. Players started performing consistently in district and state-level competitions. The academy strengthened its commitment to long-term athlete development by offering progressive training paths for beginners, intermediate players, and advanced athletes.'
    },
    {
      year: '2020',
      title: 'Resilience and Adaptation',
      description: 'The year 2020 tested resilience across the sports world. Despite challenges, Imperial Badminton Academy remained committed to player development. Training methodologies were adapted with a focus on fitness maintenance, discipline, and mental conditioning. This period reinforced the academy’s belief that true growth comes through consistency, patience, and adaptability.'
    },
    {
      year: '2021',
      title: 'Restart and Rebuild',
      description: 'With renewed energy, 2021 marked a strong comeback. Training intensity increased, and players returned with improved focus and motivation. Special attention was given to rebuilding match fitness and confidence.  The academy witnessed renewed enthusiasm among players, leading to improved performance in competitive events.'
    },
    {
      year: '2022',
      title: 'Strengthening the Academy Structure',
      description: 'In 2022, Imperial Badminton Academy further strengthened its training ecosystem. Coaching techniques were upgraded, training schedules became more structured, and player monitoring improved.'
    },
    {
      year: '2023',
      title: 'Developing Future Champions',
      description: 'The academy focused heavily on youth and grassroots development in 2023. Young players were trained with long-term vision, ensuring strong fundamentals and progressive skill growth. Several trainees demonstrated significant improvement, reflecting the academy’s commitment to quality training and disciplined practice.'
    },

    {
      year: '2024',
      title: 'Performance & Consistency',
      description: 'By 2024, Imperial Badminton Academy had built a reputation for consistency and professionalism. Players showcased disciplined gameplay, improved match awareness, and competitive confidence. The academy continued to refine its training philosophy, focusing on balanced development—technical, physical, and mental.'
    },

    {
      year: '2025',
      title: 'Excellence Through Experience',
      description: 'In 2025, years of experience translated into maturity and excellence. The academy emphasized advanced training modules, competitive exposure, and performance analysis. Imperial Badminton Academy strengthened its position as a trusted name for serious badminton training, attracting dedicated players committed to growth and success.'
    },
    {
      year: '2026',
      title: 'A Vision for the Future',
      description: 'As of 2026, Imperial Badminton Academy stands as a symbol of dedication, growth, and excellence. With a clear vision for the future, the academy continues to evolve by embracing modern training techniques, nurturing young talent, and preparing players for higher levels of competition. Our journey from 2010 to 2026 reflects our unwavering commitment to shaping skilled athletes, confident individuals, and future champions.'
    },
  ];

  return (
    <>
      <Helmet>
        <title>Our History</title>
      </Helmet>
      <div className="history-page">
        <div className="page-header">
          <div className="container">
            <h1>Our History</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/about">About</Link>
              <span>/</span>
              <span>Our History</span>
            </div>
          </div>
        </div>

        <section className="history-intro section">
          <div className="container">
            <div className="story-grid">
              <div className="story-image">
                <img src="https://images.unsplash.com/photo-1599391398131-cd12dfc6c24e?w=600" alt="Our History" />
              </div>
              <div className="story-content">
                <span className="section-subtitle">Our Journey</span>
                <h2>From Humble Beginnings to Excellence</h2>
                <p>
                  The story of Imperial Badminton Academy is one of passion, perseverance, and
                  an unwavering commitment to excellence. What started as a dream in 2010 has
                  grown into one of the most respected badminton training institutions in India.
                </p>
                <p>
                  Our founder, Coach Rajesh Kumar, a former national champion himself, envisioned
                  an academy that would not just teach badminton but would transform lives through
                  sport. With limited resources but unlimited determination, he laid the foundation
                  of what Imperial stands for today.
                </p>
                <p>
                  Over the years, we have witnessed countless success stories - from beginners
                  discovering their love for the sport to champions standing on podiums at
                  national and international events. Each story adds a new chapter to our
                  rich history.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="timeline-section section bg-light">
          <div className="container">
            <div className="section-title">
              <h2>Our Journey Through The Years</h2>
              <p>
                Key moments that shaped Imperial Badminton Academy into what it is today.
              </p>
            </div>
            <div className="timeline">
              {milestones.map((milestone, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-year">{milestone.year}</div>
                  <div className="timeline-content">
                    <h3>{milestone.title}</h3>
                    <p>{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="about-cta section bg-light">
          <div className="container">
            <div className="cta-box">
              <h2>Be Part of Our Story</h2>
              <p>
                Join Imperial Badminton Academy and become part of a legacy of excellence.
                Your journey to becoming a champion starts here.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-accent">
                  Start Your Journey <FaArrowRight />
                </Link>
                <Link to="/testimonials" className="btn btn-secondary">
                  Read Success Stories
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default History;
